import { useState } from "react";
import { useParams } from "react-router-dom";
import type { UseFormReturnType } from "@mantine/form";
import { v4 as uuidv4 } from "uuid";

import type { Crossbar } from "crossbar/types/crossbar.model";
import type { Event } from "event/types/event.model";
import {
  Container,
  LoadingOverlay,
  Space,
  Text,
  useMantineTheme,
} from "@mantine/core";

import { useDebug } from "common/debug/hooks/useDebug";
import { useGetCrossbarEventQuery } from "crossbar/api/crossbar-event.api";
import { useGetCrossbarParticipationWinnersQuery } from "crossbar/api/crossbar-participation.api";
import { CrossbarEventForm } from "./CrossbarEventForm";
import CrossbarWinner from "./CrossbarWinner";

export function CrossbarEventPresenter({
  crossbar,
  crossbarEventId,
  eventForm,
  organization,
}: {
  crossbar?: Crossbar;
  crossbarEventId?: string;
  eventForm: UseFormReturnType<
    Omit<Event, "id">,
    (values: Omit<Event, "id">) => Omit<Event, "id">
  >;
  organization: string;
}) {
  const { isDebugMode } = useDebug();
  const { eventId } = useParams<{ eventId: string }>();
  const { data: crossbarEventData, refetch: refetchCrossbarEvent } =
    useGetCrossbarEventQuery(crossbarEventId!, { skip: !crossbarEventId });
  const { data: winnersData } = useGetCrossbarParticipationWinnersQuery(
    {
      crossbarEventId: crossbarEventId!,
    },
    { skip: !crossbarEventId },
  );
  const [isLoading, setIsLoading] = useState(false);
  const theme = useMantineTheme();

  return (
    <>
      {isLoading ? (
        <div className="h-100 w-100 d-flex align-items-center justify-content-center">
          <div className="spinner-border" role="status">
            <LoadingOverlay visible={true} overlayOpacity={0} />
          </div>
        </div>
      ) : null}
      {eventId && crossbarEventData ? (
        <div style={{ fontFamily: theme.fontFamily }}>
          {crossbarEventData.max_winners === undefined ? (
            <Container style={{ color: "red", marginTop: "1rem" }}>
              <Text>Attention</Text>
              <Text>The number of winners has not been initialized</Text>
            </Container>
          ) : null}
          <CrossbarEventForm
            crossbar={crossbar}
            crossbarEvent={crossbarEventData}
            editMode={true}
            eventForm={eventForm}
            eventId={eventId}
            organization={organization}
            refetchCrossbarEvent={refetchCrossbarEvent}
            setIsLoading={setIsLoading}
          />

          <Container style={{ marginTop: "1rem" }}>
            <Text weight="bolder">Crossbar event ID :</Text>{" "}
            {crossbarEventData.id}
            <Space h="md" />
            {winnersData?.winners ? (
              <>
                <Text weight="bolder">List of Winners :</Text>
                {winnersData.winners.map((winner) => (
                  <>
                    <CrossbarWinner key={uuidv4()} winner={winner} />
                    <Space h="md" />
                  </>
                ))}
              </>
            ) : null}
            {winnersData?.winners?.length === 0 ? (
              <div>
                <Text>No winner yet</Text>
              </div>
            ) : null}
          </Container>
          {isDebugMode ? (
            <Container style={{ marginTop: "1rem" }}>
              <Text weight="bolder">Crossbar event data :</Text>
              <pre>{JSON.stringify(crossbarEventData, null, 2)}</pre>
              <Space h="md" />
              <Text weight="bolder">Crossbar event winners data :</Text>
              <pre>{JSON.stringify(winnersData, null, 2)}</pre>
              <Space h="md" />
            </Container>
          ) : null}
        </div>
      ) : null}
      {eventId && !crossbarEventData ? (
        <CrossbarEventForm
          crossbar={crossbar}
          editMode={false}
          eventForm={eventForm}
          eventId={eventId}
          organization={organization}
          refetchCrossbarEvent={refetchCrossbarEvent}
          setIsLoading={setIsLoading}
        />
      ) : null}
    </>
  );
}
